import React from 'react';

import { faExpand } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { PERMISSION } from 'src/features/auth';
import AddNewParkingModal from 'src/features/parkingAdmin/components/AddNewParkingModal';
import { ParkingStatus } from 'src/features/parkingAdmin/components/ParkingStatus';
import { badgeYesNoOptions } from 'src/features/parkingAdmin/utils/constants';
import parkingAdminMessages from 'src/features/parkingAdmin/utils/messages';
import { useGetParkings } from 'src/features/parkingManagement/api/getParkings';
import { useGetCityList } from 'src/features/parkingManagement/api/useGetCityList';
import useHasPermissions from 'src/hooks/useHasPermissions';
import usePagination from 'src/hooks/usePagination';
import useSearch from 'src/hooks/useSearch';
import {
    CellBadge,
    DataTable,
    DataTableColumnGenericHeaderDefinition,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { CellLink } from 'src/registry/components/table/cells/CellLink';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import { Button } from 'src/registry/ui/Button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/registry/ui/Tooltip';
import { generalMessages } from 'src/utils/messages';

import type { Parking } from 'src/features/parkingManagement/types/parking';

const ParkingAdmin = () => {
    const { formatMessage } = useIntl();
    const navigate = useNavigate();
    const permissionParkingAdminSettings = useHasPermissions([PERMISSION.PARKING_ADMIN_SETTINGS]);

    const { queryPaginationOptions, ...pagination } = usePagination({});
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'name' });

    const { data, isLoading, isError } = useGetParkings({
        ...queryPaginationOptions,
        ...querySearchOptions,
    });

    const { data: cityList } = useGetCityList({});

    const cityOptions = React.useMemo(
        () =>
            cityList?.results.map((city) => ({
                label: city.name,
                value: city.id,
            })),
        [cityList],
    );

    const columnHelper = createColumnHelper<Parking>();

    const columns = [
        columnHelper.accessor('name', {
            header: DataTableColumnHeader,
            cell: (props) => (
                <CellLink to={`/parking-management/${props.row.original.id}/general-settings`} {...props} />
            ),
            meta: {
                title: formatMessage(generalMessages.name),
            },
            minSize: 200,
        }),
        columnHelper.accessor('system_id', {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(parkingAdminMessages.systemId),
            },
        }),
        columnHelper.accessor('city', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                const rowCityId = row.original.city;
                const city = cityList?.results.find((city) => city.id === rowCityId);

                return city?.name || '-';
            },
            meta: {
                title: formatMessage(generalMessages.city),
                options: cityOptions,
            },
        }),
        columnHelper.accessor('id', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: (props) => <ParkingStatus parkingId={props.row.original.id} {...props} />,
            meta: {
                title: formatMessage(parkingAdminMessages.status),
            },
        }),
        columnHelper.accessor('is_public', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: (props) => <CellBadge badgeOptions={badgeYesNoOptions} {...props} />,
            meta: {
                title: formatMessage(parkingAdminMessages.publicParking),
                badgeOptions: badgeYesNoOptions,
            },
        }),
        columnHelper.accessor('is_featured', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: (props) => <CellBadge badgeOptions={badgeYesNoOptions} {...props} />,
            meta: {
                title: formatMessage(parkingAdminMessages.isFeatured),
                badgeOptions: badgeYesNoOptions,
            },
        }),
        columnHelper.accessor('enable_online_payments', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: (props) => <CellBadge badgeOptions={badgeYesNoOptions} {...props} />,
            meta: {
                title: formatMessage(parkingAdminMessages.onlinePayments),
                badgeOptions: badgeYesNoOptions,
            },
        }),
        columnHelper.accessor('enable_online_reservations', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: (props) => <CellBadge badgeOptions={badgeYesNoOptions} {...props} />,
            meta: {
                title: formatMessage(parkingAdminMessages.reservations),
                badgeOptions: badgeYesNoOptions,
            },
        }),
        columnHelper.display({
            header: DataTableColumnGenericHeaderDefinition,
            id: 'actions',
            cell: (props) => (
                <div className="ml-auto flex max-w-24 gap-1">
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    onClick={() =>
                                        navigate(`/parking-management/${props.row.original.id}/general-settings`)
                                    }
                                    size="icon"
                                    variant="link"
                                >
                                    <FontAwesomeIcon icon={faExpand} className="cursor-pointer" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent side="top">{formatMessage(parkingAdminMessages.parking)}</TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </div>
            ),
            maxSize: 45,
        }),
    ];

    return (
        <DataTable
            columns={columns}
            data={data}
            semantics={{
                title: formatMessage(parkingAdminMessages.parkings),
                entryName: formatMessage(parkingAdminMessages.parking),
                entryNames: formatMessage(parkingAdminMessages.parkingsCount),
            }}
            pagination={pagination}
            search={search}
            isError={isError}
            isLoading={isLoading}
            getRowId={(row) => row.id.toString()}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                newEntry: permissionParkingAdminSettings ? AddNewParkingModal : undefined,
            }}
        />
    );
};

export default ParkingAdmin;
